import Service from '@ember/service';
import { service } from '@ember/service';
import type AnalyticsService from '@ebiz/ember-aditools-analytics/services/aditools-analytics';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import type LoggingService from './logger';
import type ModelService from './model';
import { StateModel } from './model';
import { Changeset } from 'ember-changeset';
import { saveDesignAnalytics, loadDesignAnalytics } from '../utils/analytics';
import type OpampService from '../services/opamp';
import { PdStage } from 'photodiode-model/lib/src/model';
import { BufferedChangeset, ValidatorAction } from 'ember-changeset/types';

type opamp = {
  opAmp: string | null;
  stage: string;
  found: boolean;
  locked: boolean;
  valid: boolean;
};

export default class PersistenceService extends Service {
  @service('aditools-analytics') declare analytics: AnalyticsService;
  @service('aditools-site') declare siteconfig: SiteService;
  @service declare logger: LoggingService;
  @service declare model: ModelService;
  @service('opamp') declare opampService: OpampService;

  getDesign(): Record<string, unknown> {
    return this.normalize(this.model.state);
  }

  getOpAmps(): opamp[] {
    const data = this.model.state;
    let opAmps = [];
    try {
      opAmps.push({
        stage: 'stage_1',
        opAmp: this.model.opAmp1 ? this.model.opAmp1.partNumber : null,
        locked: data.OpAmp1 ? true : false,
        found: true,
        valid: true,
      });
      if (this.model.opAmp2) {
        opAmps.push({
          stage: 'stage_2',
          opAmp: this.model.opAmp2 ? this.model.opAmp2.partNumber : null,
          locked: data.OpAmp2 ? true : false,
          found: true,
          valid: true,
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: unknown) {
      opAmps = [];
    }
    return opAmps;
  }

  loadDesign(data: string): Promise<{
    updatedChangeSet?: BufferedChangeset;
    status: number;
    message: string | undefined;
  }> {
    const parsedData = JSON.parse(data);
    return Promise.resolve()
      .then(() => {
        if (!this.model.opamps) {
          if (this.opampService.opAmps) {
            return this.opampService.opAmps.then((opamps) => {
              if (opamps !== null) {
                this.model.opamps = opamps;
              } else {
                throw { status: -3, message: `No OpAmps` };
              }
              return Promise.resolve();
            });
          }
        }
        return;
      })
      .then(() => {
        const designData = parsedData.Design.photodiode;
        const modelStateChangeset = Changeset(this.model.state, this.model.validateLoadDesign as ValidatorAction);
        try {
          modelStateChangeset.set('Photodiode', designData.sensor);
          modelStateChangeset.set('Vr', parseFloat(designData.reverseVoltage));
          modelStateChangeset.set('Bias', designData.biasPreference === 'positive' ? '+' : '-');
          modelStateChangeset.set('Cd', parseFloat(designData.capacitance));
          modelStateChangeset.set('Rsh', parseFloat(designData.shuntResistance));
          modelStateChangeset.set('Ip', parseFloat(designData.peakCurrent));
          modelStateChangeset.set('StageCount', parseFloat(designData.stages));
          modelStateChangeset.set(
            'OpAmp1',
            designData.opAmps.length > 0 && designData.opAmps[0].locked ? designData.opAmps[0].opAmp : null,
          );
          modelStateChangeset.set(
            'OpAmp2',
            designData.opAmps.length > 1 && designData.opAmps[1].locked ? designData.opAmps[1].opAmp : null,
          );
          modelStateChangeset.set('Vp', parseFloat(designData.peakVoltage));
          modelStateChangeset.set(
            'SpeedPreference',
            designData.speedPreference === 'bandwidth'
              ? 'Bandwidth'
              : designData.speedPreference === 'minPulseWidth'
                ? 'PulseWidth'
                : designData.speedPreference,
          );
          modelStateChangeset.set(
            'TargetSpeed',
            designData.speedPreference === 'minPulseWidth'
              ? 2 / parseFloat(designData.speed)
              : parseFloat(designData.speed),
          );
          modelStateChangeset.set('Peaking', parseFloat(designData.peaking));
          modelStateChangeset.set(
            'FrequencyResponseUnit',
            designData.frequencyResponseUnit ? designData.frequencyResponseUnit : 'dB',
          );
          modelStateChangeset.set('NoiseGainUnit', designData.noiseGainUnit ? designData.noiseGainUnit : 'V/V');
        } catch {
          // Exception occurs when changeset data cannot be populated because design data does not exist for said field
          // This is an invalid design case.
          throw 'Invalid Design';
        }
        return modelStateChangeset;
      })
      .then((modelStateChangeset) => {
        return modelStateChangeset.validate().then(() => {
          let status = 0;
          let message: string | undefined = '';
          // Check if there are State Validation Errors
          if (modelStateChangeset.errors.length > 0) {
            const opAmpErrors = modelStateChangeset.errors.filter(
              (item) => item.key === 'OpAmp1' || item.key === 'OpAmp2',
            );
            if (opAmpErrors.length > 0) {
              const opAmpList: string[] = [];
              opAmpErrors.forEach((item) => {
                opAmpList.push(item.value);
              });
              // OpAmp not Supported Error
              status = -2;
              message = opAmpList.join();
            } else {
              // General Error
              status = -1;
              message = modelStateChangeset.errors[0] ? modelStateChangeset.errors[0].validation.toString() : '';
            }
          } else {
            // Check if there are Model Errors
            const newState = new StateModel();
            newState.Photodiode = modelStateChangeset['Photodiode'] as string;
            newState.Vr = modelStateChangeset['Vr'] as number;
            newState.Bias = modelStateChangeset['Bias'] as '+' | '-';
            newState.Cd = modelStateChangeset['Cd'] as number;
            newState.Rsh = modelStateChangeset['Rsh'] as number;
            newState.Ip = modelStateChangeset['Ip'] as number;
            newState.StageCount = modelStateChangeset['StageCount'] as PdStage;
            newState.OpAmp1 = modelStateChangeset['OpAmp1'] as string | null;
            newState.OpAmp2 = modelStateChangeset['OpAmp2'] as string | null;
            newState.Vp = modelStateChangeset['Vp'] as number;
            newState.TargetSpeed = modelStateChangeset['TargetSpeed'] as number;
            newState.SpeedPreference = modelStateChangeset['SpeedPreference'] as string;
            newState.Peaking = modelStateChangeset['Peaking'] as number;
            newState.FrequencyResponseUnit = modelStateChangeset['FrequencyResponseUnit'] as string;
            newState.NoiseGainUnit = modelStateChangeset['NoiseGainUnit'] as string;

            const modelInvalid = this.model.validateModel(newState);
            const isMarginalDesign =
              modelInvalid && modelInvalid.ErrorParameter && modelInvalid.ErrorParameter.notRecommendedAvailable;
            if (!modelInvalid || isMarginalDesign) {
              loadDesignAnalytics(parsedData, this.analytics);
              status = 0;
              message = '';
            } else {
              status = -1;
              message = 'model-error.' + modelInvalid.ErrorCode;
            }
          }
          return {
            updatedChangeSet: modelStateChangeset,
            status: status,
            message: message,
          };
        });
      })
      .catch((error) => {
        // Return Exception when Error
        return {
          status: error.status ? error.status : -1,
          message: `${error.message ? error.message : error}`,
        };
      });
  }

  normalize(data: StateModel): Record<string, unknown> {
    const opAmps = this.model.opamps ? this.getOpAmps() : [];
    return {
      photodiode: {
        sensor: data.Photodiode,
        reverseVoltage: data.Vr,
        biasPreference: data.Bias === '+' ? 'positive' : 'negative',
        capacitance: data.Cd,
        shuntResistance: data.Rsh,
        peakCurrent: data.Ip,
        peakVoltage: data.Vp,
        speedPreference:
          data.SpeedPreference === 'Bandwidth'
            ? 'bandwidth'
            : data.SpeedPreference === 'PulseWidth'
              ? 'minPulseWidth'
              : data.SpeedPreference,
        speed: data.SpeedPreference === 'Bandwidth' ? data.TargetSpeed : 2 / data.TargetSpeed,
        peaking: data.Peaking,
        stages: opAmps.length.toString(),
        opAmps: opAmps,
        frequencyResponseUnit: data.FrequencyResponseUnit === 'dB' ? 'dB' : 'V/A',
        noiseGainUnit: data.NoiseGainUnit === 'dB' ? 'dB' : 'V/V',
      },
    };
  }

  saveDesign(name: string, description: string): void {
    const data = this.getDesign();
    const now = new Date();
    const ticks = now.getTime();
    const fileName = (name.length > 0 ? name : 'SavedDesignPD' + ticks.toString()) + '.json';
    const saveDateStamp =
      now.getFullYear() +
      '-' +
      ('0' + (now.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + now.getDate()).slice(-2) +
      ' ' +
      ('0' + (((now.getHours() + 11) % 12) + 1)).slice(-2) +
      ':' +
      ('0' + now.getMinutes()).slice(-2) +
      ':' +
      ('0' + now.getSeconds()).slice(-2) +
      ' ' +
      (now.getHours() >= 12 ? 'PM' : 'AM');
    const toolName = 'PD';
    const jsonVersion = '1.0.0';

    const designData = {
      Name: name,
      Description: description,
      Tool: toolName,
      Design: data,
      CreatedDate: saveDateStamp,
      UpdatedDate: saveDateStamp,
      JsonVersion: jsonVersion,
    };

    const jsonStringData = JSON.stringify(designData, null, '\t');
    // console.log(jsonStringData);

    const blob = new Blob([jsonStringData], {
      type: 'application/octet-stream; charset=UTF-8',
    });

    if (typeof window.navigator.msSaveBlob !== 'undefined') {
      window.navigator.msSaveBlob(blob, fileName);
    } else {
      const URL = window.URL || window.webkitURL;
      const downloadUrl = URL.createObjectURL(blob);

      if (fileName) {
        const a = document.createElement('a');
        if (typeof a.download === 'undefined') {
          window.location.href = downloadUrl;
        } else {
          a.href = downloadUrl;
          a.download = fileName;
          a.click();
        }
      } else {
        window.location.href = downloadUrl;
      }
      URL.revokeObjectURL(downloadUrl);
    }
    saveDesignAnalytics(this.model, this.analytics);
  }
}

declare module '@ember/service' {
  interface Registry {
    persistence: PersistenceService;
  }
}
